/**
 * Готовые блоки для конструктора сигналов.
 *
 * Каталог величин отвечает на вопрос «что сравнить». Этот отвечает на
 * вопрос «что описать»: трейдер думает не «RSI, оператор, число», а
 * «перепроданность», «пробой максимума», «объём выше обычного». Блок —
 * это готовая мысль с разумными значениями, которую можно поправить.
 *
 * **Каждый блок объявляет свою природу — событие или состояние.** Это
 * не подсказка для оформления, а то, что определяет, куда его можно
 * положить.
 *
 *   событие    случается в одну свечу: пересечение, пробой, разворот
 *   состояние  верно неделями: цена выше EMA, RSI в зоне, тренд вверх
 *
 * Событие в контексте бессмысленно (оно почти всегда ложно), состояние
 * в триггере даёт сигнал на каждом баре растущего тренда. Конструктор
 * поэтому предлагает в каждое место только то, что там осмысленно, — и
 * человеку не нужно знать это правило, чтобы его соблюдать.
 */
import { RULE_INDICATORS } from './indicators.js';
import { MORE_BLOCKS } from './blocks-more.js';
import { isScriptEvent } from './script.js';
export const BLOCK_CATEGORIES = [
    /*
     * Структура идёт первой не по алфавиту.
     *
     * Тот, кто открывает конструктор ради «снял ликвидность → слом → FVG»,
     * ищет это первым; тому, кому нужен RSI, спуститься на два раздела
     * ниже проще, чем первому — найти структуру среди скользящих средних.
     */
    { id: 'structure', title: 'Структура' },
    { id: 'liquidity', title: 'Ликвидность' },
    { id: 'zone', title: 'Зоны' },
    { id: 'price', title: 'Цена' },
    { id: 'trend', title: 'Тренд' },
    { id: 'momentum', title: 'Импульс' },
    { id: 'volume', title: 'Объём' },
    { id: 'volatility', title: 'Волатильность' },
    { id: 'candle', title: 'Свечи' },
];
const close = { kind: 'price', source: 'close' };
const high = { kind: 'price', source: 'high' };
const low = { kind: 'price', source: 'low' };
function indicator(id, params, output) {
    return { kind: 'indicator', indicator: id, params, ...(output ? { output } : {}) };
}
const ema = (period) => indicator('ema', { period });
const BASE_BLOCKS = [
    /* ------------------------------------------------------------- цена */
    {
        id: 'price-above-ema',
        label: 'Цена выше EMA 200',
        hint: 'Крупный тренд смотрит вверх. Обычный фон для покупок.',
        category: 'price',
        nature: 'state',
        make: () => ({ left: close, operator: 'above', right: ema(200) }),
    },
    {
        id: 'price-below-ema',
        label: 'Цена ниже EMA 200',
        hint: 'Крупный тренд смотрит вниз.',
        category: 'price',
        nature: 'state',
        make: () => ({ left: close, operator: 'below', right: ema(200) }),
    },
    {
        id: 'price-cross-ema-up',
        label: 'Цена пересекла EMA 50 вверх',
        hint: 'Момент перелома, а не состояние: случается в одну свечу.',
        category: 'price',
        nature: 'event',
        make: () => ({ left: close, operator: 'cross_above', right: ema(50) }),
    },
    {
        id: 'price-cross-ema-down',
        label: 'Цена пересекла EMA 50 вниз',
        hint: 'Зеркальный перелом вниз.',
        category: 'price',
        nature: 'event',
        make: () => ({ left: close, operator: 'cross_below', right: ema(50) }),
    },
    {
        id: 'price-above-vwap',
        label: 'Цена выше VWAP',
        hint: 'Торгуется дороже средней цены дня — покупатели сильнее.',
        category: 'price',
        nature: 'state',
        make: () => ({ left: close, operator: 'above', right: indicator('vwap', { period: 20 }) }),
    },
    {
        id: 'breakout-high',
        label: 'Пробой максимума 20 баров',
        hint: 'Цена ушла выше всего, что было за период.',
        category: 'price',
        nature: 'event',
        make: () => ({
            left: high,
            operator: 'cross_above',
            // Максимум прошлых баров — верхняя полоса канала по своей сути.
            right: indicator('bollinger', { period: 20, deviations: 2 }, 'upper'),
        }),
    },
    {
        id: 'breakdown-low',
        label: 'Пробой минимума 20 баров',
        hint: 'Цена ушла ниже всего, что было за период.',
        category: 'price',
        nature: 'event',
        make: () => ({
            left: low,
            operator: 'cross_below',
            right: indicator('bollinger', { period: 20, deviations: 2 }, 'lower'),
        }),
    },
    /* ------------------------------------------------------------ тренд */
    {
        id: 'ema-cross-up',
        label: 'EMA 50 пересекла EMA 200 вверх',
        hint: 'Классический разворот тренда наверх.',
        category: 'trend',
        nature: 'event',
        make: () => ({ left: ema(50), operator: 'cross_above', right: ema(200) }),
    },
    {
        id: 'ema-cross-down',
        label: 'EMA 50 пересекла EMA 200 вниз',
        hint: 'Тот же разворот в другую сторону.',
        category: 'trend',
        nature: 'event',
        make: () => ({ left: ema(50), operator: 'cross_below', right: ema(200) }),
    },
    {
        id: 'supertrend-long',
        label: 'Supertrend смотрит вверх',
        hint: 'Цена выше линии Supertrend: тренд считается восходящим.',
        category: 'trend',
        nature: 'state',
        make: () => ({
            left: close,
            operator: 'above',
            right: indicator('supertrend', { period: 10, multiplier: 3 }),
        }),
    },
    {
        id: 'adx-strong',
        label: 'Тренд сильный (ADX выше 25)',
        hint: 'Движение направленное, а не топтание на месте.',
        category: 'trend',
        nature: 'state',
        make: () => ({
            left: indicator('adx', { period: 14 }),
            operator: 'above',
            right: { kind: 'number', value: 25 },
        }),
    },
    /* ---------------------------------------------------------- импульс */
    {
        id: 'rsi-oversold',
        label: 'RSI перепродан (ниже 30)',
        hint: 'Состояние, а не событие: RSI может сидеть там долго.',
        category: 'momentum',
        nature: 'state',
        make: () => ({
            left: indicator('rsi', { period: 14 }),
            operator: 'below',
            right: { kind: 'number', value: 30 },
        }),
    },
    {
        id: 'rsi-overbought',
        label: 'RSI перекуплен (выше 70)',
        hint: 'Зеркальное состояние наверху.',
        category: 'momentum',
        nature: 'state',
        make: () => ({
            left: indicator('rsi', { period: 14 }),
            operator: 'above',
            right: { kind: 'number', value: 70 },
        }),
    },
    {
        id: 'rsi-cross-30-up',
        label: 'RSI пересёк 30 снизу вверх',
        hint: 'Выход из перепроданности — момент, а не состояние. Частый триггер покупки.',
        category: 'momentum',
        nature: 'event',
        make: () => ({
            left: indicator('rsi', { period: 14 }),
            operator: 'cross_above',
            right: { kind: 'number', value: 30 },
        }),
    },
    {
        id: 'rsi-cross-70-down',
        label: 'RSI пересёк 70 сверху вниз',
        hint: 'Выход из перекупленности.',
        category: 'momentum',
        nature: 'event',
        make: () => ({
            left: indicator('rsi', { period: 14 }),
            operator: 'cross_below',
            right: { kind: 'number', value: 70 },
        }),
    },
    {
        id: 'macd-cross-up',
        label: 'MACD пересёк сигнальную вверх',
        hint: 'Импульс развернулся наверх.',
        category: 'momentum',
        nature: 'event',
        make: () => ({
            left: indicator('macd', { fast: 12, slow: 26, signal: 9 }, 'line'),
            operator: 'cross_above',
            right: indicator('macd', { fast: 12, slow: 26, signal: 9 }, 'signal'),
        }),
    },
    {
        id: 'macd-bullish',
        label: 'MACD выше сигнальной',
        hint: 'Импульс уже наверху — годится как подтверждение.',
        category: 'momentum',
        nature: 'state',
        make: () => ({
            left: indicator('macd', { fast: 12, slow: 26, signal: 9 }, 'line'),
            operator: 'above',
            right: indicator('macd', { fast: 12, slow: 26, signal: 9 }, 'signal'),
        }),
    },
    {
        id: 'stochastic-cross-20',
        label: 'Stochastic пересёк 20 вверх',
        hint: 'Выход из нижней зоны.',
        category: 'momentum',
        nature: 'event',
        make: () => ({
            left: indicator('stochastic', { period: 14 }),
            operator: 'cross_above',
            right: { kind: 'number', value: 20 },
        }),
    },
    /* ------------------------------------------------------------ объём */
    {
        id: 'volume-above-average',
        label: 'Объём выше среднего',
        hint: 'За движением стоят реальные деньги, а не тонкий рынок.',
        category: 'volume',
        nature: 'state',
        make: () => ({
            left: { kind: 'volume' },
            operator: 'above',
            right: indicator('volume_sma', { period: 20 }),
        }),
    },
    {
        id: 'volume-spike',
        label: 'Объём в 1.5 раза выше обычного',
        hint: 'Всплеск интереса. Хорошее подтверждение пробоя.',
        category: 'volume',
        nature: 'state',
        make: () => ({
            left: indicator('relative_volume', { period: 20 }),
            operator: 'above',
            right: { kind: 'number', value: 1.5 },
        }),
    },
    {
        id: 'volume-double',
        label: 'Объём вдвое выше обычного',
        hint: 'Редкое событие: обычно сопровождает новость.',
        category: 'volume',
        nature: 'state',
        make: () => ({
            left: indicator('relative_volume', { period: 20 }),
            operator: 'above',
            right: { kind: 'number', value: 2 },
        }),
    },
    /* ----------------------------------------------------- волатильность */
    {
        id: 'bollinger-breakout-up',
        label: 'Пробой верхней полосы Боллинджера',
        hint: 'Цена вышла за пределы обычного размаха.',
        category: 'volatility',
        nature: 'event',
        make: () => ({
            left: close,
            operator: 'cross_above',
            right: indicator('bollinger', { period: 20, deviations: 2 }, 'upper'),
        }),
    },
    {
        id: 'bollinger-breakout-down',
        label: 'Пробой нижней полосы Боллинджера',
        hint: 'Цена ушла ниже обычного размаха.',
        category: 'volatility',
        nature: 'event',
        make: () => ({
            left: close,
            operator: 'cross_below',
            right: indicator('bollinger', { period: 20, deviations: 2 }, 'lower'),
        }),
    },
    {
        id: 'atr-rising',
        label: 'Волатильность выросла на 20%',
        hint: 'Рынок ожил. Помогает отсечь сигналы в спящем рынке.',
        category: 'volatility',
        nature: 'state',
        make: () => ({
            left: indicator('atr', { period: 14 }),
            operator: 'changed_by',
            right: { kind: 'number', value: 20 },
        }),
    },
    /* -------------------------------------------------------- структура */
    {
        id: 'bos-up',
        label: 'Пробой структуры вверх',
        hint: 'Цена ушла за прошлую вершину: рынок продолжает движение наверх.',
        category: 'structure',
        nature: 'event',
        make: () => ({
            left: indicator('bos', { left: 5, right: 5 }, 'up'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'bos-down',
        label: 'Пробой структуры вниз',
        hint: 'Цена ушла под прошлую впадину.',
        category: 'structure',
        nature: 'event',
        make: () => ({
            left: indicator('bos', { left: 5, right: 5 }, 'down'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'choch-up',
        label: 'Слом характера вверх',
        hint: 'Первый пробой вверх после нисходящей структуры — рынок передумал.',
        category: 'structure',
        nature: 'event',
        make: () => ({
            left: indicator('choch', { left: 5, right: 5 }, 'up'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'choch-down',
        label: 'Слом характера вниз',
        hint: 'Зеркальный слом после восходящей структуры.',
        category: 'structure',
        nature: 'event',
        make: () => ({
            left: indicator('choch', { left: 5, right: 5 }, 'down'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'trend-up',
        label: 'Структура смотрит вверх',
        hint: 'Состояние, а не момент: рынок делает более высокие вершины.',
        category: 'structure',
        nature: 'state',
        make: () => ({
            left: indicator('trend_structure', { left: 5, right: 5 }),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'discount',
        label: 'Цена в дискаунте',
        hint: 'Нижняя половина диапазона — дёшево относительно размаха.',
        category: 'structure',
        nature: 'state',
        make: () => ({
            left: indicator('premium', { left: 5, right: 5 }),
            operator: 'below',
            right: { kind: 'number', value: 50 },
        }),
    },
    {
        id: 'premium-zone',
        label: 'Цена в премиуме',
        hint: 'Верхняя половина диапазона — дорого относительно размаха.',
        category: 'structure',
        nature: 'state',
        make: () => ({
            left: indicator('premium', { left: 5, right: 5 }),
            operator: 'above',
            right: { kind: 'number', value: 50 },
        }),
    },
    /* ------------------------------------------------------ ликвидность */
    {
        id: 'sweep-high',
        label: 'Снятие ликвидности сверху',
        hint: 'Тень зашла за вершину, а закрытие вернулось: стопы собраны, движения нет.',
        category: 'liquidity',
        nature: 'event',
        make: () => ({
            left: indicator('sweep', { left: 5, right: 5 }, 'high'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'sweep-low',
        label: 'Снятие ликвидности снизу',
        hint: 'То же под впадиной — частое начало разворота вверх.',
        category: 'liquidity',
        nature: 'event',
        make: () => ({
            left: indicator('sweep', { left: 5, right: 5 }, 'low'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    /* ------------------------------------------------------------- зоны */
    {
        id: 'fvg-open',
        label: 'Есть незакрытый разрыв снизу',
        hint: 'Промежуток, который рынок проскочил и обычно возвращается закрыть.',
        category: 'zone',
        nature: 'state',
        make: () => ({
            left: indicator('fvg', { side: 1, size: 0.25 }, 'active'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'fvg-retest',
        label: 'Возврат в разрыв',
        hint: 'Цена вернулась к промежутку — момент входа в этой школе.',
        category: 'zone',
        nature: 'event',
        make: () => ({
            /*
             * Событие берётся у самого индикатора, а не выводится из границ
             * зоны. Разрыв закрывается ровно тем баром, который в него
             * вернулся, — и к моменту сравнения границы уже нет.
             */
            left: indicator('fvg', { side: 1, size: 0.25 }, 'touched'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'order-block-retest',
        label: 'Возврат в ордер-блок',
        hint: 'Цена вернулась туда, откуда начался импульс.',
        category: 'zone',
        nature: 'event',
        make: () => ({
            left: indicator('order_block', { side: 1, strength: 1.5 }, 'touched'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    /* ------------------------------------------------------------ свечи */
    {
        id: 'engulfing-up',
        label: 'Бычье поглощение',
        hint: 'Свеча перекрыла телом предыдущую красную: покупатель перехватил ход.',
        category: 'candle',
        nature: 'event',
        make: () => ({
            left: indicator('engulfing', { factor: 1 }, 'up'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'engulfing-down',
        label: 'Медвежье поглощение',
        hint: 'Зеркальная фигура после зелёной свечи.',
        category: 'candle',
        nature: 'event',
        make: () => ({
            left: indicator('engulfing', { factor: 1 }, 'down'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'pin-bar-up',
        label: 'Пин-бар с тенью снизу',
        hint: 'Цену увели вниз и вернули внутри одной свечи: отказ идти дальше.',
        category: 'candle',
        nature: 'event',
        make: () => ({
            left: indicator('pin_bar', { factor: 2 }, 'up'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'pin-bar-down',
        label: 'Пин-бар с тенью сверху',
        hint: 'То же наверху — частый разворот вниз.',
        category: 'candle',
        nature: 'event',
        make: () => ({
            left: indicator('pin_bar', { factor: 2 }, 'down'),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'inside-bar',
        label: 'Внутренний бар',
        hint: 'Свеча внутри предыдущей: рынок сжался, движение обычно рядом.',
        category: 'candle',
        nature: 'event',
        make: () => ({
            left: indicator('inside_bar', {}),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    },
    {
        id: 'doji',
        label: 'Доджи',
        hint: 'Открытие и закрытие сошлись: равновесие, никто не победил.',
        category: 'candle',
        nature: 'event',
        make: () => ({
            left: indicator('doji', { share: 0.1 }),
            operator: 'above',
            right: { kind: 'number', value: 0 },
        }),
    }
];
/** Весь каталог: первые сорок два и вторая волна (`blocks-more.ts`). */
export const SIGNAL_BLOCKS = [...BASE_BLOCKS, ...MORE_BLOCKS];
/** Блоки, осмысленные в этом месте сигнала. */
export function blocksFor(nature) {
    return SIGNAL_BLOCKS.filter((block) => block.nature === nature);
}
export function blockById(id) {
    return SIGNAL_BLOCKS.find((block) => block.id === id) ?? null;
}
/**
 * Событие это или состояние.
 *
 * Различие определяет, что с условием вообще можно сделать. «Сначала
 * сняли ликвидность, потом слом структуры» — про события: они
 * случаются в одну свечу, и между ними есть время. Состояние
 * («цена выше EMA») верно неделями, и говорить «оно случилось раньше»
 * бессмысленно — оно верно и сейчас, иначе его бы не проверяли.
 *
 * Определяется по форме условия, а не по каталогу: блок, поправленный
 * человеком, остаётся тем же по сути, и терять его природу от смены
 * порога нельзя.
 */
/** Индикаторы, дающие ноль или единицу: сработало или нет. */
const FLAG_INDICATORS = new Set(['bos', 'choch', 'sweep']);
/** Выходы-флаги у индикаторов, которые обычно дают цену. */
const FLAG_OUTPUTS = new Set(['touched']);
export function isEventCondition(condition) {
    // Пересечение — событие по определению: оно требует двух баров и
    // выполняется ровно на одном.
    if (condition.operator === 'cross_above' || condition.operator === 'cross_below')
        return true;
    const left = condition.left;
    const base = left.kind === 'recent' || left.kind === 'previous' || left.kind === 'scaled'
        ? left.of
        : left;
    if (base.kind !== 'indicator')
        return false;
    if (FLAG_INDICATORS.has(base.indicator))
        return true;
    if (isScriptEvent(base.indicator, base.output))
        return true;
    if (base.output !== undefined && FLAG_OUTPUTS.has(base.output))
        return true;
    // Остальные флаги из реестра: пин-бар, поглощение, дивергенция, свежая зона…
    // «Случилось» — это момент, а не длительность.
    const spec = RULE_INDICATORS.find((entry) => entry.id === base.indicator);
    if (!spec)
        return false;
    if (spec.flag)
        return true;
    // «Зона не закрыта» — длительность, а не момент.
    if (base.output === 'active')
        return false;
    return base.output !== undefined && spec.outputs?.find((entry) => entry.key === base.output)?.flag === true;
}
/** Окно условия в барах. `null` — проверяется на текущем баре. */
export function windowOf(condition) {
    return condition.left.kind === 'recent' ? condition.left.bars : null;
}
/**
 * Поставить или снять окно «за последние N баров».
 *
 * `null` возвращает условие к проверке на текущем баре. Окно ставится
 * только событиям: у состояния «максимум за окно» — не то же самое, что
 * «было верно хоть раз», и подменять одно другим значило бы посчитать
 * не то, что человек прочитал.
 */
export function withWindow(condition, bars) {
    const left = condition.left;
    const base = left.kind === 'recent' ? left.of : left;
    if (bars === null)
        return { ...condition, left: base };
    if (!isEventCondition(condition))
        return condition;
    return { ...condition, left: { kind: 'recent', of: base, bars: Math.max(1, bars) } };
}
//# sourceMappingURL=blocks.js.map